import { ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  StoreOperationsAnalyticsResType,
  StoreOperationsAnalyticsInfoResType,
  StoreOperationsAlertInfoResType
} from '@/models/types/storeOperationsAnalytics.types'

/**
 * v2/Store/operations-analytics 的結果暫存
 *
 * 這支 API 一次回傳此帳號底下所有店家的資料，只在首頁呼叫一次：
 * - 首頁：用 storesOperationsAlertInfos 的 key 對應 storeId，把風險計數併進店家清單顯示
 * - 店家頁：用 storeOperationsAnalyticsInfos 的 key 對應 storeId 取出該店的營運分析
 *
 * 持久化到 localStorage，讓使用者直接開店家頁連結／重新整理時仍讀得到（資料同時受
 * 首頁重新呼叫覆蓋）。
 */
export const useStoreOperationsAnalyticsStore = defineStore(
  'storeOperationsAnalytics',
  () => {
    const analyticsByStoreId = ref<Record<string, StoreOperationsAnalyticsInfoResType>>({})
    const alertByStoreId = ref<Record<string, StoreOperationsAlertInfoResType>>({})
    // 門檻設定更新後，店家頁會直接呼叫端重新打 operations-analytics（見 UpdateStoreModal.vue），
    // 這裡只是給呼叫端讀寫的 loading 旗標，store 本身不擁有 fetch 邏輯
    const isRefreshing = ref(false)

    const setAnalytics = (data: StoreOperationsAnalyticsResType) => {
      analyticsByStoreId.value = data.storeOperationsAnalyticsInfos ?? {}
      alertByStoreId.value = data.storesOperationsAlertInfos ?? {}
    }

    const getStoreAnalytics = (storeId: string) => analyticsByStoreId.value[storeId]

    const getStoreAlert = (storeId: string) => alertByStoreId.value[storeId]

    const reset = () => {
      analyticsByStoreId.value = {}
      alertByStoreId.value = {}
    }

    return {
      analyticsByStoreId,
      alertByStoreId,
      isRefreshing,
      setAnalytics,
      getStoreAnalytics,
      getStoreAlert,
      reset
    }
  },
  {
    persist: true
  }
)
